import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoursePreview } from '../components/CoursePreview';
import './BrowseCoursesPage.scss';

interface Course {
  title: string;
  creator: string;
  source: string;
  modules: number;
  duration: string;
  color: string;
  tags?: string[];
  description?: string;
  difficulty?: string;
}

export const BrowseCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trending');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const tabs = [
    { id: 'trending', name: 'Trending' },
    { id: 'online-business', name: 'Online Business' },
    { id: 'books', name: 'Books' },
    { id: 'government', name: 'Government' },
    { id: 'social-media', name: 'Social Media' },
    { id: 'fashion', name: 'Fashion' },
    { id: 'health-fitness', name: 'Health & Fitness' },
    { id: 'technology', name: 'Technology' },
    { id: 'science', name: 'Science' },
    { id: 'arts', name: 'Arts & Design' },
    { id: 'music', name: 'Music' },
    { id: 'finance', name: 'Finance' },
    { id: 'psychology', name: 'Psychology' },
  ];

  const trendingData = [
    {
      category: 'Trending',
      courses: [
        {
          title: 'Debate Like Charlie Kirk: Conservative Campus Strategy',
          creator: 'Charlie Kirk',
          source: 'Turning Point USA',
          modules: 10,
          duration: '6 hours',
          students: '52.8k',
          rating: 4.7,
          color: 'red',
          tags: [
            'Politics',
            'Debate',
            'Conservative Thought',
            'Campus Activism',
          ],
          description:
            'Master the art of political debate and learn strategies for defending conservative principles on college campuses.',
        },
        {
          title: 'Advanced AI Prompt Engineering',
          creator: 'OpenAI Research Team',
          source: 'OpenAI',
          modules: 8,
          duration: '12 hours',
          students: '89.3k',
          rating: 4.9,
          color: 'purple',
          tags: ['AI', 'Machine Learning', 'Prompt Engineering', 'ChatGPT'],
          description:
            'Learn advanced techniques for getting the best results from AI models and language models.',
        },
        {
          title: 'Cryptocurrency Trading Psychology',
          creator: 'Diamond Hands Academy',
          source: 'Crypto Veterans',
          modules: 15,
          duration: '20 hours',
          students: '67.2k',
          rating: 4.6,
          color: 'gold',
          tags: ['Crypto', 'Trading', 'Psychology', 'Risk Management'],
          description:
            'Master the mental game of cryptocurrency trading and avoid common psychological pitfalls.',
        },
        {
          title: 'TikTok Algorithm Mastery',
          creator: 'Social Media Insider',
          source: 'Creator Economy',
          modules: 6,
          duration: '4 hours',
          students: '124.7k',
          rating: 4.8,
          color: 'pink',
          tags: ['Social Media', 'TikTok', 'Algorithm', 'Content Creation'],
          description:
            'Crack the TikTok algorithm code and build a massive following with viral content strategies.',
        },
        {
          title: 'Remote Work Productivity Hacking',
          creator: 'Digital Nomad Collective',
          source: 'Remote Work Institute',
          modules: 9,
          duration: '8 hours',
          students: '78.4k',
          rating: 4.7,
          color: 'blue',
          tags: [
            'Remote Work',
            'Productivity',
            'Time Management',
            'Work-Life Balance',
          ],
          description:
            'Transform your home into a productivity powerhouse and master the art of remote work.',
        },
      ],
    },
    {
      category: 'Aspirational Careers',
      courses: [
        {
          title: 'Path to E10 Meta Engineer',
          creator: 'Ex-Meta Staff Engineers',
          source: 'FAANG Insiders',
          modules: 25,
          duration: '120 hours',
          students: '12.3k',
          rating: 4.9,
          color: 'blue',
          tags: ['Software Engineering', 'Meta', 'System Design', 'Leadership'],
          description:
            'Complete roadmap from junior engineer to E10 Staff Engineer at Meta, including system design, leadership, and technical excellence.',
        },
        {
          title: 'Fortune 500 CEO Track',
          creator: 'Executive Leadership Institute',
          source: 'CEO Network',
          modules: 40,
          duration: '200 hours',
          students: '3.8k',
          rating: 4.8,
          color: 'gold',
          tags: ['Leadership', 'Strategy', 'Business', 'Executive'],
          description:
            'Learn the strategic thinking, leadership skills, and business acumen needed to reach C-suite positions.',
        },
        {
          title: 'Venture Capital Partner Pipeline',
          creator: 'Andreessen Horowitz Alumni',
          source: 'VC Masters',
          modules: 18,
          duration: '90 hours',
          students: '5.7k',
          rating: 4.7,
          color: 'green',
          tags: ['Venture Capital', 'Investment', 'Startups', 'Finance'],
          description:
            'Master deal sourcing, due diligence, and portfolio management to become a top-tier VC partner.',
        },
        {
          title: 'NASA Mission Director Path',
          creator: 'Former NASA Leadership',
          source: 'Space Program Veterans',
          modules: 30,
          duration: '150 hours',
          students: '2.1k',
          rating: 4.9,
          color: 'navy',
          tags: ['Aerospace', 'Leadership', 'NASA', 'Mission Management'],
          description:
            'Learn the complex skills needed to lead billion-dollar space missions and guide humanity to the stars.',
        },
        {
          title: 'Supreme Court Justice Preparation',
          creator: 'Constitutional Law Scholars',
          source: 'Legal Elite Academy',
          modules: 50,
          duration: '300 hours',
          students: '1.2k',
          rating: 4.8,
          color: 'navy',
          tags: ['Law', 'Constitutional Law', 'Supreme Court', 'Judicial'],
          description:
            'Master constitutional law, judicial philosophy, and legal reasoning for the highest court in the land.',
        },
        {
          title: 'Professional Athlete to Hall of Fame',
          creator: 'Sports Performance Institute',
          source: 'Champion Athletes',
          modules: 35,
          duration: '180 hours',
          students: '8.9k',
          rating: 4.6,
          color: 'red',
          tags: ['Sports', 'Performance', 'Mental Training', 'Elite Athletics'],
          description:
            'Learn the mental, physical, and strategic elements that separate good athletes from Hall of Fame legends.',
        },
      ],
    },
  ];

  const categoryData = {
    'online-business': [
      {
        category: 'E-commerce & Dropshipping',
        courses: [
          {
            title: 'Amazon FBA Private Label Mastery',
            creator: 'Sarah Johnson',
            source: 'Amazon Seller Central',
            modules: 14,
            duration: '6 hours',
            color: 'orange',
            tags: ['Amazon', 'FBA', 'E-commerce'],
            description:
              'Complete guide to building a profitable Amazon FBA business from product research to scaling.',
          },
          {
            title: 'Shopify Store Optimization & CRO',
            creator: 'Marcus Chen',
            source: 'Shopify Partners',
            modules: 12,
            duration: '5 hours',
            color: 'green',
            tags: ['Shopify', 'CRO', 'E-commerce'],
            description:
              'Maximize your Shopify store conversions with advanced optimization techniques and A/B testing.',
          },
          {
            title: 'Print on Demand Empire Building',
            creator: 'Lisa Martinez',
            source: 'Industry Case Studies',
            modules: 10,
            duration: '4 hours',
            color: 'purple',
            tags: ['POD', 'Design', 'Passive Income'],
            description:
              'Create a sustainable print-on-demand business using platforms like Printful and Etsy.',
          },
          {
            title: 'Affiliate Marketing Funnel Systems',
            creator: 'David Park',
            source: 'Marketing Frameworks',
            modules: 16,
            duration: '7 hours',
            color: 'blue',
            tags: ['Affiliate', 'Marketing', 'Funnels'],
            description:
              'Build high-converting affiliate marketing funnels that generate passive income streams.',
          },
        ],
      },
      {
        category: 'Digital Products & Services',
        courses: [
          {
            title: 'SaaS Launch Strategy & Growth Hacking',
            creator: 'Emma Wilson',
            source: 'Startup Case Studies',
            modules: 18,
            duration: '8 hours',
            color: 'cyan',
            tags: ['SaaS', 'Growth Hacking', 'Startups'],
            description:
              'From MVP to $1M ARR: proven strategies for launching and scaling software products.',
          },
          {
            title: 'Online Course Creation & Marketing',
            creator: 'Michael Torres',
            source: 'Education Platforms',
            modules: 14,
            duration: '6 hours',
            color: 'indigo',
            tags: ['Online Courses', 'Education', 'Marketing'],
            description:
              'Create, market, and sell profitable online courses using modern educational technology.',
          },
          {
            title: 'Freelancing Business Systems',
            creator: 'Anna Rodriguez',
            source: 'Freelancer Networks',
            modules: 11,
            duration: '5 hours',
            color: 'pink',
            tags: ['Freelancing', 'Business Systems', 'Client Management'],
            description:
              'Scale your freelancing into a profitable agency with systems, processes, and team building.',
          },
        ],
      },
    ],
    books: [
      {
        category: 'Literary Classics',
        courses: [
          {
            title: 'Pride and Prejudice by Jane Austen',
            creator: 'AI Instructor',
            source: 'Classic Literature',
            modules: 14,
            duration: '6 hours',
            color: 'rose',
            tags: ['Romance', 'Social Commentary', 'Victorian Literature'],
            description:
              "Explore themes of love, class, and social expectations in Austen's beloved romantic masterpiece.",
          },
          {
            title: 'The Great Gatsby by F. Scott Fitzgerald',
            creator: 'AI Instructor',
            source: 'American Literature',
            modules: 12,
            duration: '5 hours',
            color: 'gold',
            tags: ['American Dream', '1920s', 'Literary Symbolism'],
            description:
              "Analyze the decay of the American Dream through Fitzgerald's critique of Jazz Age excess.",
          },
          {
            title: 'One Hundred Years of Solitude by Gabriel García Márquez',
            creator: 'AI Instructor',
            source: 'Magical Realism',
            modules: 16,
            duration: '7 hours',
            color: 'orange',
            tags: [
              'Magical Realism',
              'Latin American Literature',
              'Family Saga',
            ],
            description:
              'Journey through seven generations of the Buendía family in this Nobel Prize-winning masterpiece.',
          },
          {
            title: 'Crime and Punishment by Fyodor Dostoevsky',
            creator: 'AI Instructor',
            source: 'Russian Literature',
            modules: 18,
            duration: '8 hours',
            color: 'red',
            tags: [
              'Psychological Fiction',
              'Russian Literature',
              'Moral Philosophy',
            ],
            description:
              "Delve into the psychological torment and moral redemption in Dostoevsky's psychological thriller.",
          },
          {
            title: 'Wuthering Heights by Emily Brontë',
            creator: 'AI Instructor',
            source: 'Gothic Literature',
            modules: 13,
            duration: '5 hours',
            color: 'purple',
            tags: ['Gothic Romance', 'Passion', 'Revenge'],
            description:
              'Explore the destructive power of obsessive love in this dark Gothic romance.',
          },
        ],
      },
      {
        category: 'Philosophy & Ancient Wisdom',
        courses: [
          {
            title: 'The Art of War by Sun Tzu',
            creator: 'AI Instructor',
            source: 'Ancient Philosophy',
            modules: 13,
            duration: '4 hours',
            color: 'indigo',
            tags: ['Strategy', 'Leadership', 'Ancient Wisdom'],
            description:
              "Apply Sun Tzu's strategic principles to modern business, leadership, and life challenges.",
          },
          {
            title: 'Meditations by Marcus Aurelius',
            creator: 'AI Instructor',
            source: 'Stoic Philosophy',
            modules: 12,
            duration: '5 hours',
            color: 'cyan',
            tags: ['Stoicism', 'Philosophy', 'Personal Growth'],
            description:
              "Learn Stoic principles for resilience, wisdom, and inner peace from a Roman Emperor's personal journal.",
          },
          {
            title: 'The Republic by Plato',
            creator: 'AI Instructor',
            source: 'Classical Philosophy',
            modules: 20,
            duration: '9 hours',
            color: 'blue',
            tags: ['Political Philosophy', 'Justice', 'Ancient Greece'],
            description:
              "Examine Plato's vision of the ideal state and explore fundamental questions about justice and governance.",
          },
          {
            title: 'Thus Spoke Zarathustra by Friedrich Nietzsche',
            creator: 'AI Instructor',
            source: 'German Philosophy',
            modules: 15,
            duration: '7 hours',
            color: 'brown',
            tags: ['Existentialism', 'Nihilism', 'German Philosophy'],
            description:
              "Confront Nietzsche's radical ideas about morality, religion, and the creation of personal values.",
          },
        ],
      },
      {
        category: 'Modern Bestsellers & Business Books',
        courses: [
          {
            title: 'Atomic Habits by James Clear',
            creator: 'AI Instructor',
            source: 'Behavioral Science',
            modules: 11,
            duration: '4 hours',
            color: 'orange',
            tags: ['Habits', 'Productivity', 'Behavior Change'],
            description:
              "Build good habits and break bad ones using Clear's proven system of tiny changes and compound growth.",
          },
          {
            title: 'Think and Grow Rich by Napoleon Hill',
            creator: 'AI Instructor',
            source: 'Success Literature',
            modules: 13,
            duration: '5 hours',
            color: 'gold',
            tags: ['Success', 'Mindset', 'Wealth Building'],
            description:
              "Master the 13 principles of wealth creation and success mindset from Napoleon Hill's timeless classic.",
          },
          {
            title: 'The 7 Habits of Highly Effective People by Stephen Covey',
            creator: 'AI Instructor',
            source: 'Leadership Development',
            modules: 7,
            duration: '4 hours',
            color: 'green',
            tags: ['Productivity', 'Leadership', 'Personal Development'],
            description:
              "Develop character-based leadership and effectiveness through Covey's principle-centered approach.",
          },
          {
            title: 'Sapiens by Yuval Noah Harari',
            creator: 'AI Instructor',
            source: 'Historical Analysis',
            modules: 20,
            duration: '8 hours',
            color: 'pink',
            tags: ['History', 'Anthropology', 'Human Evolution'],
            description:
              "Explore the history of humanity from the Stone Age to the modern era through Harari's unique lens.",
          },
        ],
      },
    ],
    government: [
      {
        category: 'Public Policy & Administration',
        courses: [
          {
            title: 'Policy Analysis & Implementation',
            creator: 'Dr. Patricia Adams',
            source: 'Government Sources',
            modules: 15,
            duration: '6 hours',
            color: 'blue',
            tags: ['Policy Analysis', 'Government', 'Public Admin'],
            description:
              'Learn systematic approaches to analyzing and implementing effective public policies.',
          },
          {
            title: 'Digital Government Transformation',
            creator: 'Alex Thompson',
            source: 'Gov Tech Reports',
            modules: 12,
            duration: '5 hours',
            color: 'green',
            tags: ['Digital Government', 'GovTech', 'Innovation'],
            description:
              'Modernize government services through digital transformation and citizen-centric design.',
          },
          {
            title: 'Municipal Finance & Budgeting',
            creator: 'Sandra Williams',
            source: 'Municipal Guidelines',
            modules: 14,
            duration: '6 hours',
            color: 'orange',
            tags: ['Municipal Finance', 'Budgeting', 'Local Government'],
            description:
              'Master local government financial management, budgeting processes, and fiscal policy.',
          },
          {
            title: 'Legislative Process & Advocacy',
            creator: 'James Carter',
            source: 'Congressional Records',
            modules: 11,
            duration: '5 hours',
            color: 'purple',
            tags: ['Legislation', 'Advocacy', 'Congress'],
            description:
              'Understand how laws are made and learn effective advocacy strategies for policy change.',
          },
        ],
      },
      {
        category: 'International Relations & Security',
        courses: [
          {
            title: 'Cybersecurity Policy & Governance',
            creator: 'Dr. Michael Zhang',
            source: 'Security Frameworks',
            modules: 16,
            duration: '7 hours',
            color: 'red',
            tags: ['Cybersecurity', 'Policy', 'Governance'],
            description:
              'Develop national cybersecurity policies and governance frameworks for critical infrastructure.',
          },
          {
            title: 'Diplomatic Protocol & International Law',
            creator: 'Ambassador Sarah Davis',
            source: 'Diplomatic Sources',
            modules: 13,
            duration: '6 hours',
            color: 'cyan',
            tags: ['Diplomacy', 'International Law', 'Protocol'],
            description:
              'Master diplomatic protocols and international legal frameworks for global relations.',
          },
        ],
      },
    ],
    'social-media': [
      {
        category: 'Content Strategy & Creation',
        courses: [
          {
            title: 'TikTok Marketing for Business Growth',
            creator: 'Ashley Kim',
            source: 'TikTok Creator Fund',
            modules: 12,
            duration: '4 hours',
            color: 'pink',
            tags: ['TikTok', 'Content Creation', 'Business Growth'],
            description:
              "Leverage TikTok's algorithm to build brand awareness and drive business growth.",
          },
          {
            title: 'Instagram Reels & Stories Mastery',
            creator: 'Carlos Rivera',
            source: 'Instagram Business',
            modules: 10,
            duration: '4 hours',
            color: 'purple',
            tags: ['Instagram', 'Reels', 'Stories'],
            description:
              'Create viral Instagram content that builds engaged communities and drives conversions.',
          },
          {
            title: 'YouTube Channel Monetization Strategy',
            creator: 'Jessica Wang',
            source: 'YouTube Creator Academy',
            modules: 14,
            duration: '6 hours',
            color: 'red',
            tags: ['YouTube', 'Monetization', 'Content Strategy'],
            description:
              'Build and monetize a successful YouTube channel with proven content and business strategies.',
          },
          {
            title: 'LinkedIn Personal Branding for Professionals',
            creator: 'Mark Sullivan',
            source: 'LinkedIn Learning',
            modules: 9,
            duration: '3 hours',
            color: 'blue',
            tags: ['LinkedIn', 'Personal Branding', 'Professional'],
            description:
              'Establish thought leadership and generate leads through strategic LinkedIn content marketing.',
          },
        ],
      },
      {
        category: 'Social Media Analytics & ROI',
        courses: [
          {
            title: 'Social Media Analytics & Attribution',
            creator: 'Rachel Martinez',
            source: 'Analytics Platforms',
            modules: 11,
            duration: '5 hours',
            color: 'green',
            tags: ['Analytics', 'Attribution', 'ROI'],
            description:
              'Measure and optimize social media ROI using advanced analytics and attribution models.',
          },
          {
            title: 'Influencer Marketing Campaign Management',
            creator: 'Tyler Johnson',
            source: 'Influencer Platforms',
            modules: 13,
            duration: '5 hours',
            color: 'orange',
            tags: [
              'Influencer Marketing',
              'Campaign Management',
              'Brand Partnerships',
            ],
            description:
              'Plan, execute, and measure successful influencer marketing campaigns for brand growth.',
          },
        ],
      },
    ],
    fashion: [
      {
        category: 'Iconic Fashion Designers',
        courses: [
          {
            title: 'Jacquemus: Minimalist Vision & Mediterranean Influence',
            creator: 'Fashion History Institute',
            source: 'Designer Archives',
            modules: 12,
            duration: '5 hours',
            color: 'rose',
            tags: ['Jacquemus', 'Minimalism', 'French Fashion'],
            description:
              "Explore Simon Porte Jacquemus's revolutionary approach to minimalist design and his celebration of Provence.",
            difficulty: 'Intermediate',
          },
          {
            title: 'Virgil Abloh: Streetwear Meets High Fashion',
            creator: 'Contemporary Fashion Studies',
            source: 'Design Analysis',
            modules: 16,
            duration: '7 hours',
            color: 'orange',
            tags: ['Virgil Abloh', 'Streetwear', 'Off-White'],
            description:
              "Analyze Virgil Abloh's groundbreaking fusion of street culture and luxury fashion at Off-White and Louis Vuitton.",
            difficulty: 'Advanced',
          },
          {
            title: 'Christian Dior: The New Look Revolution',
            creator: 'Fashion Legacy Foundation',
            source: 'Historical Archives',
            modules: 14,
            duration: '6 hours',
            color: 'purple',
            tags: ['Christian Dior', 'New Look', 'Couture'],
            description:
              'Discover how Christian Dior revolutionized post-war fashion with the New Look and shaped modern couture.',
            difficulty: 'Beginner',
          },
          {
            title: 'Coco Chanel: Liberation Through Fashion',
            creator: 'Chanel Institute',
            source: 'Brand Archives',
            modules: 15,
            duration: '6 hours',
            color: 'indigo',
            tags: ['Coco Chanel', 'Liberation', 'Timeless Design'],
            description:
              'Learn how Gabrielle Chanel freed women from restrictive fashion and created timeless, elegant designs.',
            difficulty: 'Intermediate',
          },
        ],
      },
      {
        category: 'Fashion Business & Innovation',
        courses: [
          {
            title: 'Sustainable Fashion Design Principles',
            creator: 'Isabella Chen',
            source: 'Sustainable Fashion Council',
            modules: 14,
            duration: '6 hours',
            color: 'green',
            tags: ['Sustainable Fashion', 'Design', 'Eco-Friendly'],
            description:
              'Design environmentally conscious fashion using sustainable materials and ethical production methods.',
            difficulty: 'Intermediate',
          },
          {
            title: 'Fashion Tech: 3D Design & Virtual Fitting',
            creator: 'Marco Rossi',
            source: 'Fashion Tech Labs',
            modules: 16,
            duration: '7 hours',
            color: 'cyan',
            tags: ['Fashion Tech', '3D Design', 'Virtual Fitting'],
            description:
              'Use cutting-edge technology to design, prototype, and fit garments in virtual environments.',
            difficulty: 'Advanced',
          },
          {
            title: 'Luxury Brand Marketing & Positioning',
            creator: 'Sophia Laurent',
            source: 'Luxury Brand Case Studies',
            modules: 12,
            duration: '5 hours',
            color: 'gold',
            tags: ['Luxury Brands', 'Marketing', 'Brand Positioning'],
            description:
              'Learn the strategies behind successful luxury fashion brands and premium positioning.',
            difficulty: 'Intermediate',
          },
          {
            title: 'Fashion E-commerce & Digital Marketing',
            creator: 'Ryan Clarke',
            source: 'Fashion E-commerce',
            modules: 11,
            duration: '4 hours',
            color: 'pink',
            tags: ['Fashion E-commerce', 'Digital Marketing', 'Online Retail'],
            description:
              'Build and market a successful fashion e-commerce business with proven digital strategies.',
            difficulty: 'Beginner',
          },
        ],
      },
    ],
    music: [
      {
        category: 'Legendary Music Producers',
        courses: [
          {
            title: 'Rick Rubin: The Art of Minimalist Production',
            creator: 'Music Production Academy',
            source: 'Producer Archives',
            modules: 18,
            duration: '8 hours',
            color: 'indigo',
            tags: ['Rick Rubin', 'Minimalist Production', 'Hip-Hop'],
            description:
              "Learn Rick Rubin's philosophy of stripping music to its essence and his work with Johnny Cash, Jay-Z, and Red Hot Chili Peppers.",
            difficulty: 'Advanced',
          },
          {
            title: 'Dr. Dre: Crafting the West Coast Sound',
            creator: 'Hip-Hop Studies Institute',
            source: 'Beats & Rhymes',
            modules: 16,
            duration: '7 hours',
            color: 'blue',
            tags: ['Dr. Dre', 'West Coast Hip-Hop', 'G-Funk'],
            description:
              "Master Dr. Dre's signature G-Funk sound and his revolutionary approach to hip-hop production and artist development.",
            difficulty: 'Intermediate',
          },
          {
            title: 'Pharrell Williams: Innovation Across Genres',
            creator: 'Contemporary Music Lab',
            source: 'Sound Innovation',
            modules: 14,
            duration: '6 hours',
            color: 'orange',
            tags: ['Pharrell', 'The Neptunes', 'Pop Production'],
            description:
              "Explore Pharrell's genre-defying production style from The Neptunes era to solo success across pop, hip-hop, and R&B.",
            difficulty: 'Intermediate',
          },
          {
            title: 'Quincy Jones: The Master Arranger',
            creator: 'Jazz & Soul Foundation',
            source: 'Musical Legacy',
            modules: 20,
            duration: '9 hours',
            color: 'gold',
            tags: ['Quincy Jones', 'Jazz', 'Michael Jackson'],
            description:
              "Study Quincy Jones's legendary career from jazz arrangements to producing Michael Jackson's biggest hits.",
            difficulty: 'Advanced',
          },
        ],
      },
      {
        category: 'Classic Production Pioneers',
        courses: [
          {
            title: 'Phil Spector: The Wall of Sound',
            creator: 'Recording History Society',
            source: 'Studio Archives',
            modules: 12,
            duration: '5 hours',
            color: 'purple',
            tags: ['Phil Spector', 'Wall of Sound', '1960s Production'],
            description:
              'Deconstruct Phil Spector\'s revolutionary "Wall of Sound" technique that defined 1960s pop music production.',
            difficulty: 'Intermediate',
          },
          {
            title: "George Martin: The Fifth Beatle's Genius",
            creator: 'Abbey Road Institute',
            source: 'Beatles Sessions',
            modules: 15,
            duration: '7 hours',
            color: 'green',
            tags: ['George Martin', 'The Beatles', 'Studio Innovation'],
            description:
              "Learn George Martin's innovative studio techniques and orchestral arrangements that shaped The Beatles' sound.",
            difficulty: 'Advanced',
          },
          {
            title: 'Berry Gordy: The Motown Formula',
            creator: 'Soul Music Academy',
            source: 'Hitsville Archives',
            modules: 13,
            duration: '6 hours',
            color: 'rose',
            tags: ['Berry Gordy', 'Motown', 'Soul Music'],
            description:
              "Understand Berry Gordy's systematic approach to creating the Motown sound and launching legendary careers.",
            difficulty: 'Intermediate',
          },
          {
            title: 'Brian Eno: Ambient & Experimental Production',
            creator: 'Electronic Music Institute',
            source: 'Sonic Landscapes',
            modules: 16,
            duration: '7 hours',
            color: 'cyan',
            tags: ['Brian Eno', 'Ambient Music', 'Experimental'],
            description:
              "Explore Brian Eno's pioneering work in ambient music and his innovative studio techniques with Bowie and U2.",
            difficulty: 'Advanced',
          },
        ],
      },
    ],
    'health-fitness': [
      {
        category: 'Fitness Training & Nutrition',
        courses: [
          {
            title: 'Science-Based Strength Training',
            creator: 'Dr. Mike Rodriguez',
            source: 'Exercise Science Research',
            modules: 15,
            duration: '6 hours',
            color: 'red',
            tags: ['Strength Training', 'Exercise Science', 'Muscle Building'],
            description:
              'Apply exercise science principles to maximize strength gains and muscle development safely.',
          },
          {
            title: 'Precision Nutrition & Meal Planning',
            creator: 'Laura Mitchell',
            source: 'Nutrition Research',
            modules: 13,
            duration: '5 hours',
            color: 'green',
            tags: ['Nutrition', 'Meal Planning', 'Healthy Eating'],
            description:
              'Create personalized nutrition plans based on individual goals, preferences, and health conditions.',
          },
          {
            title: 'Functional Movement & Injury Prevention',
            creator: 'Chris Thompson',
            source: 'Physical Therapy Guidelines',
            modules: 12,
            duration: '5 hours',
            color: 'blue',
            tags: ['Functional Movement', 'Injury Prevention', 'Mobility'],
            description:
              'Improve movement quality and prevent injuries through functional training and mobility work.',
          },
          {
            title: 'High-Intensity Interval Training (HIIT)',
            creator: 'Maria Santos',
            source: 'HIIT Research Studies',
            modules: 10,
            duration: '4 hours',
            color: 'orange',
            tags: ['HIIT', 'Cardio', 'Fat Loss'],
            description:
              'Design effective HIIT workouts for maximum fat loss and cardiovascular improvement.',
          },
        ],
      },
      {
        category: 'Mental Health & Wellness',
        courses: [
          {
            title: 'Stress Management & Mindfulness',
            creator: 'Dr. Jennifer Lee',
            source: 'Mindfulness Research',
            modules: 14,
            duration: '6 hours',
            color: 'purple',
            tags: ['Stress Management', 'Mindfulness', 'Mental Health'],
            description:
              'Develop evidence-based stress management techniques and mindfulness practices for better mental health.',
          },
          {
            title: 'Sleep Optimization for Peak Performance',
            creator: 'Dr. Robert Chen',
            source: 'Sleep Science Studies',
            modules: 11,
            duration: '4 hours',
            color: 'indigo',
            tags: ['Sleep Optimization', 'Performance', 'Recovery'],
            description:
              'Optimize your sleep for better performance, recovery, and overall health using scientific principles.',
          },
          {
            title: 'Habit Formation & Behavior Change',
            creator: 'Sarah Williams',
            source: 'Behavioral Psychology',
            modules: 12,
            duration: '5 hours',
            color: 'cyan',
            tags: ['Habits', 'Behavior Change', 'Psychology'],
            description:
              'Use psychology-based strategies to build lasting healthy habits and break negative patterns.',
          },
        ],
      },
    ],
    technology: [
      {
        category: 'AI & Machine Learning',
        courses: [
          {
            title: 'Large Language Models Architecture',
            creator: 'Dr. Sarah Chen',
            source: 'Research Papers',
            modules: 18,
            duration: '7 hours',
            color: 'blue',
            tags: ['AI', 'LLMs', 'Transformers'],
            description:
              'Deep dive into transformer architectures and how modern LLMs process language.',
            difficulty: 'Advanced',
          },
          {
            title: 'Computer Vision with PyTorch',
            creator: 'Alex Kumar',
            source: 'GitHub + Tutorials',
            modules: 15,
            duration: '6 hours',
            color: 'purple',
            tags: ['Computer Vision', 'PyTorch', 'Deep Learning'],
            description:
              'Build state-of-the-art computer vision models using PyTorch and modern architectures.',
            difficulty: 'Intermediate',
          },
          {
            title: 'MLOps: Production ML Systems',
            creator: 'Maria Rodriguez',
            source: 'Industry Reports',
            modules: 12,
            duration: '5 hours',
            color: 'green',
            tags: ['MLOps', 'DevOps', 'Production'],
            description:
              'Deploy and maintain machine learning systems in production environments.',
            difficulty: 'Intermediate',
          },
          {
            title: 'Reinforcement Learning Algorithms',
            creator: 'Dr. James Liu',
            source: 'Academic Papers',
            modules: 20,
            duration: '8 hours',
            color: 'orange',
            tags: ['RL', 'Algorithms', 'Game Theory'],
            description:
              'Master reinforcement learning from Q-learning to advanced policy gradient methods.',
            difficulty: 'Advanced',
          },
        ],
      },
      {
        category: 'Web Development',
        courses: [
          {
            title: 'React Server Components Deep Dive',
            creator: 'John Smith',
            source: 'React Docs',
            modules: 14,
            duration: '5 hours',
            color: 'cyan',
            tags: ['React', 'SSR', 'Performance'],
            description:
              'Master React Server Components for improved performance and SEO.',
            difficulty: 'Intermediate',
          },
          {
            title: 'WebAssembly Performance Optimization',
            creator: 'Emma Wilson',
            source: 'MDN + Examples',
            modules: 10,
            duration: '4 hours',
            color: 'indigo',
            tags: ['WebAssembly', 'Performance', 'Low-Level'],
            description:
              'Optimize web applications using WebAssembly for near-native performance.',
            difficulty: 'Advanced',
          },
          {
            title: 'GraphQL Federation at Scale',
            creator: 'David Park',
            source: 'Apollo Docs',
            modules: 12,
            duration: '5 hours',
            color: 'pink',
            tags: ['GraphQL', 'Microservices', 'API'],
            description:
              'Build and scale distributed GraphQL architectures with federation.',
            difficulty: 'Advanced',
          },
          {
            title: 'Edge Computing with Cloudflare Workers',
            creator: 'Lisa Zhang',
            source: 'Cloudflare Docs',
            modules: 8,
            duration: '3 hours',
            color: 'rose',
            tags: ['Edge Computing', 'Serverless', 'CDN'],
            description:
              'Deploy serverless applications at the edge for ultra-low latency.',
            difficulty: 'Intermediate',
          },
        ],
      },
    ],
    business: [
      {
        category: 'Entrepreneurship',
        courses: [
          {
            title: 'Bootstrapping SaaS to $1M ARR',
            creator: 'Mike Foster',
            source: 'Case Studies',
            modules: 16,
            duration: '6 hours',
            color: 'green',
            tags: ['SaaS', 'Startup', 'Revenue'],
            description:
              'Build a profitable SaaS business from zero to $1M annual recurring revenue.',
          },
          {
            title: 'Supply Chain Optimization Strategies',
            creator: 'Jennifer Liu',
            source: 'Industry Reports',
            modules: 12,
            duration: '4 hours',
            color: 'blue',
            tags: ['Supply Chain', 'Logistics', 'Operations'],
            description:
              'Optimize supply chain operations for efficiency and cost reduction.',
          },
          {
            title: 'Digital Marketing Attribution Models',
            creator: 'Carlos Rodriguez',
            source: 'Marketing Blogs',
            modules: 10,
            duration: '4 hours',
            color: 'orange',
            tags: ['Marketing', 'Analytics', 'Attribution'],
            description:
              'Master multi-touch attribution models for better marketing ROI.',
          },
          {
            title: 'Venture Capital Term Sheet Analysis',
            creator: 'Amanda Chen',
            source: 'Legal Documents',
            modules: 8,
            duration: '3 hours',
            color: 'purple',
            tags: ['VC', 'Fundraising', 'Legal'],
            description:
              'Understand and negotiate venture capital term sheets effectively.',
          },
        ],
      },
    ],
    science: [
      {
        category: 'Physics & Chemistry',
        courses: [
          {
            title: 'Quantum Computing Algorithms',
            creator: 'Dr. Alice Quantum',
            source: 'ArXiv Papers',
            modules: 24,
            duration: '10 hours',
            color: 'purple',
            tags: ['Quantum', 'Algorithms', 'Physics'],
            description:
              "Explore quantum algorithms from Shor's to quantum machine learning.",
          },
          {
            title: 'Organic Synthesis Mechanisms',
            creator: 'Prof. Bob Benzene',
            source: 'Chemistry Journals',
            modules: 18,
            duration: '7 hours',
            color: 'green',
            tags: ['Chemistry', 'Organic', 'Synthesis'],
            description:
              'Master organic reaction mechanisms and synthetic strategies.',
          },
          {
            title: 'Gravitational Wave Detection',
            creator: 'Dr. Carol Cosmos',
            source: 'LIGO Publications',
            modules: 16,
            duration: '6 hours',
            color: 'blue',
            tags: ['Physics', 'Astronomy', 'LIGO'],
            description:
              'Understand gravitational wave physics and detection methods.',
          },
          {
            title: 'Nanotechnology Applications',
            creator: 'Dr. Dave Nano',
            source: 'Nature Materials',
            modules: 14,
            duration: '5 hours',
            color: 'cyan',
            tags: ['Nanotech', 'Materials', 'Engineering'],
            description: 'Explore cutting-edge applications of nanotechnology.',
          },
        ],
      },
    ],
    arts: [
      {
        category: 'Visual Arts & Design',
        courses: [
          {
            title: 'Renaissance Fresco Techniques',
            creator: 'Isabella Artisan',
            source: 'Museum Archives',
            modules: 12,
            duration: '5 hours',
            color: 'rose',
            tags: ['Art History', 'Fresco', 'Renaissance'],
            description:
              'Learn traditional fresco painting techniques from the Renaissance masters.',
          },
          {
            title: 'Digital Art with Stable Diffusion',
            creator: 'Alex Pixel',
            source: 'AI Art Tutorials',
            modules: 8,
            duration: '3 hours',
            color: 'pink',
            tags: ['AI Art', 'Stable Diffusion', 'Digital'],
            description:
              'Create stunning digital art using AI-powered tools and techniques.',
          },
          {
            title: 'Japanese Woodblock Printing',
            creator: 'Hiroshi Takeshi',
            source: 'Traditional Texts',
            modules: 10,
            duration: '4 hours',
            color: 'indigo',
            tags: ['Japanese Art', 'Printmaking', 'Traditional'],
            description:
              'Master the traditional Japanese art of woodblock printing.',
          },
          {
            title: 'Sound Design for Films',
            creator: 'Maria Sonora',
            source: 'Industry Examples',
            modules: 14,
            duration: '6 hours',
            color: 'orange',
            tags: ['Sound Design', 'Film', 'Audio'],
            description:
              'Create immersive soundscapes and effects for film production.',
          },
        ],
      },
    ],
    health: [
      {
        category: 'Medical & Wellness',
        courses: [
          {
            title: 'Immunotherapy Cancer Treatments',
            creator: 'Dr. Sarah Onco',
            source: 'Medical Journals',
            modules: 20,
            duration: '8 hours',
            color: 'blue',
            tags: ['Immunotherapy', 'Cancer', 'Medicine'],
            description:
              'Understand cutting-edge immunotherapy approaches in cancer treatment.',
          },
          {
            title: 'Microbiome & Mental Health',
            creator: 'Dr. gut Brain',
            source: 'Research Studies',
            modules: 16,
            duration: '6 hours',
            color: 'green',
            tags: ['Microbiome', 'Mental Health', 'Gut-Brain'],
            description:
              'Explore the connection between gut microbiome and mental health.',
          },
          {
            title: 'Precision Medicine Genomics',
            creator: 'Prof. DNA Sequence',
            source: 'Genomics Papers',
            modules: 18,
            duration: '7 hours',
            color: 'purple',
            tags: ['Genomics', 'Precision Medicine', 'DNA'],
            description:
              'Learn how genomics is revolutionizing personalized medicine.',
          },
          {
            title: 'Breathwork & Nervous System',
            creator: 'Zen Master Flow',
            source: 'Ancient Practices',
            modules: 8,
            duration: '3 hours',
            color: 'cyan',
            tags: ['Breathwork', 'Wellness', 'Nervous System'],
            description:
              'Master breathing techniques for nervous system regulation.',
          },
        ],
      },
    ],
    language: [
      {
        category: 'Languages & Linguistics',
        courses: [
          {
            title: 'Mandarin Business Negotiations',
            creator: 'Li Wei',
            source: 'Business Cases',
            modules: 12,
            duration: '5 hours',
            color: 'rose',
            tags: ['Mandarin', 'Business', 'Negotiation'],
            description:
              'Master business Mandarin for successful negotiations in China.',
          },
          {
            title: 'Ancient Greek Philosophy Texts',
            creator: 'Dr. Sophia Athens',
            source: 'Original Texts',
            modules: 16,
            duration: '7 hours',
            color: 'indigo',
            tags: ['Ancient Greek', 'Philosophy', 'Classics'],
            description:
              'Read and analyze philosophical texts in ancient Greek.',
          },
          {
            title: 'Sign Language Interpretation',
            creator: 'Deaf Culture Expert',
            source: 'Video Examples',
            modules: 20,
            duration: '8 hours',
            color: 'orange',
            tags: ['Sign Language', 'ASL', 'Deaf Culture'],
            description:
              'Learn American Sign Language and deaf culture awareness.',
          },
          {
            title: 'Constructed Languages (Conlangs)',
            creator: 'Prof. Lingua Create',
            source: 'Language Examples',
            modules: 10,
            duration: '4 hours',
            color: 'pink',
            tags: ['Conlangs', 'Linguistics', 'Language Creation'],
            description:
              'Design and analyze constructed languages from Esperanto to Dothraki.',
          },
        ],
      },
    ],
    math: [
      {
        category: 'Advanced Mathematics',
        courses: [
          {
            title: 'Topology & Algebraic Geometry',
            creator: 'Dr. Abstract Space',
            source: 'Mathematical Proofs',
            modules: 24,
            duration: '10 hours',
            color: 'purple',
            tags: ['Topology', 'Algebra', 'Geometry'],
            description:
              'Explore abstract mathematical structures in topology and algebraic geometry.',
          },
          {
            title: 'Game Theory in Economics',
            creator: 'Prof. Nash Equilibrium',
            source: 'Economic Papers',
            modules: 14,
            duration: '6 hours',
            color: 'blue',
            tags: ['Game Theory', 'Economics', 'Strategy'],
            description:
              'Apply game theory to economic decision-making and strategic interactions.',
          },
          {
            title: 'Cryptography & Number Theory',
            creator: 'Dr. Prime Numbers',
            source: 'Security Papers',
            modules: 18,
            duration: '7 hours',
            color: 'green',
            tags: ['Cryptography', 'Number Theory', 'Security'],
            description:
              'Master cryptographic algorithms and their mathematical foundations.',
          },
          {
            title: 'Chaos Theory & Fractals',
            creator: 'Dr. Strange Attractor',
            source: 'Mathematical Models',
            modules: 12,
            duration: '5 hours',
            color: 'cyan',
            tags: ['Chaos Theory', 'Fractals', 'Dynamics'],
            description:
              'Understand complex systems, chaos theory, and fractal geometry.',
          },
        ],
      },
    ],
    engineering: [
      {
        category: 'Engineering Applications',
        courses: [
          {
            title: 'Rocket Propulsion Systems',
            creator: 'Dr. Thrust Vector',
            source: 'NASA Documents',
            modules: 20,
            duration: '8 hours',
            color: 'orange',
            tags: ['Aerospace', 'Propulsion', 'Rockets'],
            description:
              'Design and analyze rocket propulsion systems for space exploration.',
          },
          {
            title: 'Biomechanical Prosthetics Design',
            creator: 'Prof. Cyborg Tech',
            source: 'Medical Engineering',
            modules: 16,
            duration: '6 hours',
            color: 'pink',
            tags: ['Biomechanics', 'Prosthetics', 'Medical'],
            description:
              'Engineer advanced prosthetic devices using biomechanical principles.',
          },
          {
            title: 'Sustainable Architecture Materials',
            creator: 'Green Builder Pro',
            source: 'Construction Studies',
            modules: 12,
            duration: '5 hours',
            color: 'green',
            tags: ['Architecture', 'Sustainability', 'Materials'],
            description:
              'Build eco-friendly structures with sustainable materials and techniques.',
          },
          {
            title: 'Quantum Sensor Networks',
            creator: 'Dr. Precision Measure',
            source: 'IEEE Papers',
            modules: 18,
            duration: '7 hours',
            color: 'indigo',
            tags: ['Quantum Sensors', 'IoT', 'Networks'],
            description:
              'Develop ultra-precise quantum sensor networks for scientific applications.',
          },
        ],
      },
    ],
    social: [
      {
        category: 'Social Sciences',
        courses: [
          {
            title: 'Urban Planning & Climate Resilience',
            creator: 'City Designer',
            source: 'Planning Reports',
            modules: 14,
            duration: '6 hours',
            color: 'blue',
            tags: ['Urban Planning', 'Climate', 'Resilience'],
            description:
              'Design climate-resilient cities for sustainable urban development.',
          },
          {
            title: 'Digital Anthropology Methods',
            creator: 'Dr. Culture Online',
            source: 'Research Methods',
            modules: 12,
            duration: '5 hours',
            color: 'purple',
            tags: ['Anthropology', 'Digital Culture', 'Research'],
            description:
              'Study human behavior and culture in digital environments.',
          },
          {
            title: 'Political Economy of Tech Giants',
            creator: 'Prof. Power Analysis',
            source: 'Policy Papers',
            modules: 16,
            duration: '6 hours',
            color: 'rose',
            tags: ['Political Economy', 'Big Tech', 'Policy'],
            description:
              'Analyze the economic and political power of major technology companies.',
          },
          {
            title: 'Indigenous Knowledge Systems',
            creator: 'Elder Wisdom Keeper',
            source: 'Oral Traditions',
            modules: 10,
            duration: '4 hours',
            color: 'cyan',
            tags: ['Indigenous', 'Traditional Knowledge', 'Culture'],
            description:
              'Learn from indigenous knowledge systems and traditional practices.',
          },
        ],
      },
    ],
    psychology: [
      {
        category: 'Psychology & Neuroscience',
        courses: [
          {
            title: 'Psychedelic Therapy Research',
            creator: 'Dr. Consciousness Expand',
            source: 'Clinical Trials',
            modules: 18,
            duration: '7 hours',
            color: 'pink',
            tags: ['Psychedelics', 'Therapy', 'Mental Health'],
            description:
              'Explore the therapeutic potential of psychedelics in mental health treatment.',
          },
          {
            title: 'Decision Making Under Uncertainty',
            creator: 'Prof. Cognitive Bias',
            source: 'Behavioral Studies',
            modules: 14,
            duration: '5 hours',
            color: 'indigo',
            tags: ['Decision Making', 'Psychology', 'Cognitive Bias'],
            description:
              'Understand cognitive biases and improve decision-making under uncertainty.',
          },
          {
            title: 'Trauma-Informed Care Practices',
            creator: 'Dr. Healing Path',
            source: 'Clinical Guidelines',
            modules: 16,
            duration: '6 hours',
            color: 'green',
            tags: ['Trauma', 'Mental Health', 'Therapy'],
            description:
              'Implement trauma-informed approaches in healthcare and therapy.',
          },
          {
            title: 'Social Media Psychology Effects',
            creator: 'Dr. Digital Mind',
            source: 'Recent Studies',
            modules: 12,
            duration: '4 hours',
            color: 'orange',
            tags: ['Social Media', 'Psychology', 'Digital Wellness'],
            description:
              'Analyze the psychological impact of social media on mental health.',
          },
        ],
      },
    ],
    finance: [
      {
        category: 'Finance & Economics',
        courses: [
          {
            title: 'DeFi Protocol Analysis',
            creator: 'Crypto Economist',
            source: 'Blockchain Data',
            modules: 16,
            duration: '6 hours',
            color: 'blue',
            tags: ['DeFi', 'Blockchain', 'Crypto'],
            description:
              'Analyze decentralized finance protocols and yield strategies.',
          },
          {
            title: 'Algorithmic Trading Strategies',
            creator: 'Quant Master',
            source: 'Trading Algorithms',
            modules: 20,
            duration: '8 hours',
            color: 'purple',
            tags: ['Algo Trading', 'Quant', 'Finance'],
            description:
              'Build and backtest algorithmic trading strategies for financial markets.',
          },
          {
            title: 'ESG Investment Framework',
            creator: 'Sustainable Finance Pro',
            source: 'Impact Reports',
            modules: 12,
            duration: '5 hours',
            color: 'green',
            tags: ['ESG', 'Sustainable Finance', 'Impact'],
            description:
              'Implement environmental, social, and governance investment strategies.',
          },
          {
            title: 'Central Bank Digital Currencies',
            creator: 'Dr. Monetary Policy',
            source: 'Fed Publications',
            modules: 14,
            duration: '6 hours',
            color: 'cyan',
            tags: ['CBDC', 'Monetary Policy', 'Digital Currency'],
            description:
              'Understand the future of money with central bank digital currencies.',
          },
        ],
      },
    ],
  };

  const getCurrentData = () => {
    if (activeTab === 'trending') {
      return trendingData;
    }
    return categoryData[activeTab as keyof typeof categoryData] || [];
  };

  const handleCourseClick = (course: Course) => {
    setSelectedCourse(course);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedCourse(null);
  };

  const handleEnroll = (course: Course) => {
    // Navigate to generate page or course page - for now we'll go to generate
    navigate(`/generate?topic=${encodeURIComponent(course.title)}`);
    handleClosePreview();
  };

  return (
    <div className="browse-page">
      {/* Header */}
      <section className="browse-header">
        <div className="browse-header-content">
          <h1 className="browse-title">Browse Courses</h1>
          <p className="browse-subtitle">
            Explore thousands of courses across every field.
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="browse-nav">
        <div className="nav-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </section>

      {/* Course Rows */}
      <section className="browse-course-content">
        {getCurrentData().map((section, sectionIndex) => {
          // Calculate base index for each section's courses
          const baseIndex = sectionIndex * 4; // Assuming 4 courses per section average

          return (
            <div
              key={`${activeTab}-${sectionIndex}`}
              className="browse-course-section"
            >
              <div className="browse-section-header">
                <h2>{section.category}</h2>
                <button className="view-all-btn">View All →</button>
              </div>

              <div className="browse-courses-row">
                {section.courses.map((course, courseIndex) => (
                  <div
                    key={courseIndex}
                    className="browse-course-card"
                    onClick={() => handleCourseClick(course)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div
                      className={`browse-course-header-visual ${course.color}`}
                    >
                      <div className="browse-course-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="browse-source-badge">
                        {course.source}
                      </span>
                    </div>

                    <div className="browse-course-content">
                      <div className="browse-course-header">
                        <h3 className="browse-course-title">{course.title}</h3>
                      </div>

                      <div className="browse-course-description">
                        <p>
                          {course.description ||
                            `Comprehensive course on ${course.title.toLowerCase()}`}
                        </p>
                      </div>

                      <div className="browse-course-meta">
                        <span className="browse-meta-item">
                          {course.modules} modules
                        </span>
                        <span className="browse-meta-item">
                          {course.duration}
                        </span>
                      </div>

                      {course.tags && course.tags.length > 0 && (
                        <div className="browse-course-tags">
                          {course.tags.map((tag: string, tagIndex: number) => (
                            <span
                              key={tagIndex}
                              className={`browse-tag ${tagIndex === 0 ? 'primary' : tagIndex === 1 ? 'secondary' : 'tertiary'}`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* Course Preview Modal */}
      {selectedCourse && (
        <CoursePreview
          course={selectedCourse}
          isOpen={showPreview}
          onClose={handleClosePreview}
          onEnroll={handleEnroll}
        />
      )}
    </div>
  );
};
